import { useCallback } from 'react';
import { createUseStyles } from 'react-jss';
import { ProductPriceUnit, ProductType, useCreateProduct } from 'queries';
import { Theme } from '@app/theme';
import classNames from 'classnames';
import { Button, useToast } from '@app/core';
import { Select, Input, isUUID } from '@app/form';
import { snakeCase } from 'lodash';
import { Form, Formik } from 'formik';
import { ProductAutocomple } from './ProductAutocomplete';

const useStyles = createUseStyles((theme: Theme) => ({
  product: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.marginBase,
  },
  name: {
    ...theme.fonts.label,
  },
  specific: {
    opacity: 1,
  },
  buttons: {
    display: 'flex',
    gap: theme.marginBase,
    justifyContent: 'flex-end',
  },
  spacer: {
    flex: 1,
  },
  secondary: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase * 2,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.marginBase * 2,
    border: { style: 'solid', width: 1, color: theme.colors.grayBorder },
  },
  price: {
    flex: 2,
  },
  unit: {
    flex: 1,
  },
}));

const unitOptions = [
  { label: '€', value: ProductPriceUnit.EUR },
  { label: '€ / h', value: ProductPriceUnit.EUR_FOR_ONE_HOUR_DURATION },
];

interface Props {
  productType?: ProductType;
  pricePerParticipant?: boolean;
  incentive?: number;
  duration?: number;
  className?: string;
  onCancel?: () => any;
  addProduct?: (productId: string) => any;
}

const initialValues = {
  product: undefined as { value: string; label: string } | undefined,
  priceUnit: ProductPriceUnit.EUR,
  price: 10,
  active: false,
  generated: true,
};
type Values = typeof initialValues;

export const AddProductLine = ({
  productType: productTypeProps,
  pricePerParticipant = false,
  incentive,
  duration,
  onCancel,
  className,
  addProduct,
}: Props) => {
  const classes = useStyles();
  const toast = useToast();
  const { mutateAsync: createProduct } = useCreateProduct();
  const save = useCallback(
    async ({ product: selected, ...values }: Values) => {
      try {
        if (!selected || !addProduct) {
          return;
        }

        let productId = selected.value;
        if (!isUUID(selected.value)) {
          const title = selected.value;
          const rand = Math.floor(Math.random() * 1000);
          const name = snakeCase(selected.value).toUpperCase() + '_' + rand;

          const product = await createProduct({
            name: 'GENERATED_' + name,
            internalName: title,
            title,
            type: productTypeProps,
            pricePerParticipant,
            ...values,
            price: Math.floor(values.price * 100),
          });
          productId = product.id;
        }

        await addProduct(productId);
      } catch (e) {
        toast.exception(e);
      }
    },
    [addProduct, createProduct, toast, pricePerParticipant],
  );

  return (
    <Formik onSubmit={save} initialValues={initialValues}>
      {({ values }) => (
        <Form>
          <div className={classNames(classes.product, className)}>
            <ProductAutocomple
              small
              name="product"
              incentive={incentive}
              duration={duration}
              productType={productTypeProps}
              pricePerParticipant={pricePerParticipant}
            />
            {values.product && !isUUID(values.product.value) && (
              <div className={classes.secondary}>
                <Input
                  name="price"
                  label="Prix"
                  noMargin
                  small
                  type="number"
                  containerClass={classes.price}
                />
                <Select
                  label="Unité"
                  noMargin
                  small
                  options={unitOptions}
                  name="priceUnit"
                  containerClass={classes.unit}
                />
              </div>
            )}
            <div className={classes.buttons}>
              <Button
                color="red"
                small
                type="button"
                onClick={onCancel}
                variant="transparent"
                text="Annuler"
              />
              <Button
                color="green"
                small
                type="submit"
                variant="muted"
                text="Enregistrer"
              />
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};
